<template>
  <b-container fluid>
    <h2 v-if="!dataLoaded && !loadError" class="loading-placeholder">Loading...</h2>
    <b-alert v-if="loadError" show variant="danger">
      Some of the bonus data could not be loaded. The current-period figures are recomputed
      server-side on the first request of the day and can take a while — please reload the
      page to retry.
    </b-alert>
    <b-container fluid v-if="dataLoaded">
      <b-row>
        <b-col>
          <h1>CEO Bonuses</h1>
          <b-table :fields="processedData.cols" :items="processedData.rows">
            <template #cell(semester)="data">
              <strong>{{ data.value }}</strong>
            </template>
            <template #cell()="data">
              <span v-b-tooltip.hover :title="data.value.tooltip">{{ data.value.bonus }}</span>
            </template>
          </b-table>
        </b-col>
        <b-col cols="5" class="small">
          <h2>Criteria</h2>
          <h4>Active teachers by group start period</h4>
          <ul>
            <li>Teachers <em>currently</em> active in the programme, bucketed by the period their group started: those whose groups started in this period, compared with those whose groups started a year earlier</li>
            <li>This reflects the make-up of the current cohort — it is <strong>not</strong> a measure of year-on-year programme growth (historical programme size cannot be reconstructed from current records)</li>
            <li>Bonus condition: &gt; 20%</li>
          </ul>
          <h4>Trainer bonuses</h4>
          <ul>
            <li>Percentage of trainers qualifying for a bonus in this period so far</li>
            <li>Bonus condition: &gt; 80%</li>
          </ul>
          <h4>Classroom attainment</h4>
          <ul>
            <li>Rolling classroom attainment (PPIs) up to the end of this period</li>
            <li>Bonus condition: &gt; 75%</li>
          </ul>
        </b-col>
      </b-row>
    </b-container>
  </b-container>
</template>

<script>
import { mapActions } from 'vuex';
import MIOService from '../services/MIOService';
import { formatDate } from '@/utils/MioHelpers';

export default {
  name: 'CeoBonuses',
  data() {
    return {
      dataLoaded: false,
      loadError: false,
    }
  },
  created() {
    this.semesters = this.getSemesters();
    this.getData();
  },
  methods: {
    ...mapActions({
      GET_OPTIONS: 'optionsList/GET_OPTIONS',
      GET_CLASSROOM_ATTAINMENT_SMALL: 'mio/GET_CLASSROOM_ATTAINMENT_SMALL',
      GET_ACTIVE_PROGRAMME_PHASE_TEACHERS: 'mio/GET_ACTIVE_PROGRAMME_PHASE_TEACHERS',
    }),
    async getData() {
      try {
        // Get trainers bonuses data
        await this.GET_OPTIONS();

        // Past-semester bonus data is served from cache and returns instantly, so those
        // requests can run in parallel. Current-semester requests each trigger a full
        // server-side recompute (heavy queries, serialised on the API's thread), so they
        // are issued sequentially below — firing all six at once on the first (uncached)
        // load of the day made them pile up and time out, leaving the page on "Loading...".
        const countries = this.$store.state.optionsList.countries,
          pastSemesters = this.semesters.slice(0, -1),
          currentSemester = this.semesters[this.semesters.length - 1].semester,
          [_classroomAttainment, _activeProgrammePhaseTeachers, ...pastBonuses] = await Promise.all([
            this.GET_CLASSROOM_ATTAINMENT_SMALL({ fromDate: '2019-01-01' }),
            this.GET_ACTIVE_PROGRAMME_PHASE_TEACHERS({ fromDate: formatDate(this.getPreviousYear(this.semesters[0].dates[0])) }),
            ...pastSemesters.reduce((agg, {semester}) =>
              agg.concat(countries.map(country => MIOService.getRCBonusData(country, semester))), []
            )
          ]);

        const currentBonuses = [];
        for (const country of countries) {
          currentBonuses.push(await MIOService.getRCBonusData(country, currentSemester));
        }

        const countriesBonuses = [...pastBonuses, ...currentBonuses];
        this.rcBonusDataRaw = Object.fromEntries(this.semesters.map((semester, index) => [
          semester.semester,
          countriesBonuses.slice(index * countries.length, (index + 1) * countries.length).reduce((agg, countryData) => agg.concat(countryData.data.results.bonusData.rows), [])
        ]));
        this.classroomAttainment = this.$store.state.mio.classroomAttainmentSmall.cumulativeData;
        this.activeTeachers = this.$store.state.mio.activeProgrammePhaseTeachers;

        this.dataLoaded = true;
      } catch (error) {
        this.loadError = true;
      }
    },
    getSemester(date) {
      return `${date.getFullYear()}H${date.getMonth() < 6 ? '1' : '2'}`;
    },
    getSemesterDates(date) {
      const year = date.getFullYear(),
        month = date.getMonth() + 1;
      return [new Date(year, Math.trunc((month - 1) / 6) * 6, 1), new Date(year, Math.ceil(month / 6) * 6, 0)];
    },
    getSemesters() {
      // Get last 3 semesters
      return [new Date(), new Date(), new Date()].map((date, index) => ({
        semester: this.getSemester(new Date(date.setMonth(date.getMonth() - 6 * index))),
        dates: this.getSemesterDates(date),
      })).reverse();
    },
    getPreviousYear(date) {
      return new Date(date.getFullYear() - 1, date.getMonth(), date.getDate());
    }
  },
  computed: {
    processedData: {
      get() {
        const bonusText = (metric, goal) => metric ? `${metric.toFixed(1)}% ${metric > goal ? '✅' : '❌'}` : 'n/a';
        const currentSemester = this.semesters[this.semesters.length - 1].semester,
          today = new Date(),
          currentPeriodStart = new Date(today.getFullYear(), today.getMonth() - 5, 1);
        const comparePeriodStart = this.getPreviousYear(currentPeriodStart),
          comparePeriodEnd = new Date(today.getFullYear() - 1, today.getMonth(), 1);

        // Expansion of programme
        const totalActiveTeachers = Object.values(this.activeTeachers).reduce((agg, teachers) => {
            const month = new Date(teachers.month),
              semester = this.getSemester(new Date(teachers.month));
            agg[semester] = (agg[semester] || 0) + Number(teachers.count);
            if (semester == currentSemester || month >= currentPeriodStart) {
              agg.current = (agg.current || 0) + Number(teachers.count);
            } else if (month >= comparePeriodStart && month <= comparePeriodEnd) {
              agg.compareCurrent = (agg.compareCurrent || 0) + Number(teachers.count);
            }
            return agg;
          }, {}),
          programmeExpansion = Object.fromEntries(this.semesters.map(({semester, dates}) => {
            const compareSemester = semester == currentSemester ? 'compareCurrent' : this.getSemester(this.getPreviousYear(dates[0])),
              semesterCount = totalActiveTeachers[semester == currentSemester ? 'current' : semester],
              compareCount = totalActiveTeachers[compareSemester];
            return [semester, {
              tooltip: semesterCount && compareCount ? `from ${compareCount} to ${semesterCount}` : `not yet available`,
              bonus: bonusText(semesterCount && compareCount ? (semesterCount - compareCount) * 100 / compareCount : 0, 20),
            }];
          }));

        // Trainer bonuses
        const trainerBonuses = Object.fromEntries(this.semesters.map(({semester}) => {
            const trainers = (this.rcBonusDataRaw[semester] || []).filter(person => person.type == 't'),
              trainersWithBonus = (trainers || []).filter(trainer => trainer.bonus_payable == 'yes');
            return [semester, {
              tooltip: `${trainersWithBonus.length} out of ${trainers.length} trainers`,
              bonus: bonusText(trainers.length == 0 ? 0 : trainersWithBonus.length / trainers.length * 100, 80),
            }]
          }));

        // Classroom attainment
        const ppis = ['work_displayed', 'arts', 'music', 'dress_up_box', 'nature_box', 'conservation', 'gardening', 'matching_games', 'puzzles', 'reading_corner', 'book_making', 'play_shop'];
        const classroomAttainments = Object.fromEntries(this.semesters.map(({semester, dates}) => {
          const semesterAttainments = {classrooms: 0, attainments: 0};
          Object.values(this.classroomAttainment).forEach((countryData) => {
            // Find month closest to the period's end with attainments data
            const date = new Date(dates[1]);
            let monthData;
            while (date >= this.semesters[0].dates[0]) {
              monthData = countryData[formatDate(date, true)];
              if (monthData) { break; }
              date.setMonth(date.getMonth() - 1);
            };
            if (!monthData) { return; }

            semesterAttainments.classrooms += monthData.classrooms;
            semesterAttainments.attainments += ppis.reduce((total, ppi) => total + (monthData[ppi] || 0), 0);
          });
          return [semester, {
            tooltip: `${(semesterAttainments.attainments).toFixed(0)} PPIs attained out of a potential total of ${semesterAttainments.classrooms * ppis.length}`,
            bonus: bonusText(semesterAttainments.attainments * 100 / (semesterAttainments.classrooms * ppis.length), 75),
          }];
        }));

        const output = {
          cols: [{
            key: 'semester',
            label: '',
          }, {
            key: 'programmeExpansion',
            label: 'Active teachers by group start period',
          }, {
            key: 'trainerBonuses',
            label: 'Trainer bonuses',
          }, {
            key: 'classroomAttainments',
            label: 'Classroom attainment',
          }
          ],
          rows: [...this.semesters.map(({semester}, index, semesters) => ({
            semester: index >= semesters.length - 1 ? 'Current' : semester,
            programmeExpansion: programmeExpansion[semester],
            trainerBonuses: trainerBonuses[semester],
            classroomAttainments: classroomAttainments[semester],
          }))]
        };

        return output;
      }
    },
  },
};
</script>
